import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Chip from '../../../components/chip/chip.component';
import Widget from '../../../components/widget/widget.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import LocationMap from '../../../components/location-map/location-map.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import Image from '../../../components/image/image.component';
import VisibleField from '../../../components/visible-field/visible-field.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import DealAssetUseBreakdown from '../deal-asset-use-breakdown/deal-asset-use-breakdown.component';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import {
  DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN, DEAL_TEMPLATE_KEY_LOT_SIZE,
} from '../../../constants/entity/deal-template-keys';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import {
  ASSET_CREATE, ASSET_VIEW, CONTACT_VIEW, DEAL_COMMON_EDIT,
} from '../../../constants/entity/permissions';
import { ROUTE_ASSET, ROUTE_CONTACT } from '../../../constants/system/routes';
import { MARKER_COLOR_RED } from '../../../constants/entity/marker-colors';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const DealPropertyDetails: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Property Info';
  const WIDGET_NAME: string = 'widget-deal-property-details';
  const COLUMNS_TOTAL: number = 2;

  const {
    isEditMode,
    isNewDeal,
    isSimpleView,
    dealData,
    dealAssetData,
    dealAssetPhotoFile,
    dealAssetIsPhotoDeleted,
    dealAssetUseBreakdownValues,
    dealAssetNewFieldItems,
    dealAssetUseBreakdownNewItems,
    dealAssetUseBreakdownNewFieldItems,
    dealAssetUseBreakdownDefaultProperties,
    dealBuildingDetailsData,
    assetList,
    portfolioList,
    assetStatusList,
    assetTypeList,
    contactCompanyList,
    measureUnitList,
    assetOwnershipStructureTypeList,
    dealHiddenFieldList,
    onFileChange,
    onFileDelete,
    onAssetChange,
    onAddNewAsset,
    onCancelNewAsset,
    onAddNewItem,
    onDeleteClick,
    onAddAssetType,
    onAssetUseBreakdownAddNewItem,
    onAssetUseBreakdownDeleteClick,
    onAssetUseBreakdownAddNewField,
    onAssetUseBreakdownDeleteField,
    onAssetUseBreakdownAssetTypeChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onAssetTypesChange,
    onAssetSellersChange,
    onEditModeButtonClick,
    onDealAssetSearch,
    onDealAssetSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();

  const [selectedSellers, setSelectedSellers]: [Contact[] | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [isMapVisible, setIsMapVisible]: [boolean, Function] = useState(false);
  const [isAdditionalFormVisible, setIsAdditionalFormVisible]: [boolean, Function] = useState(false);
  const [isNewAssetFormVisible, setIsNewAssetFormVisible]: [boolean, Function] = useState(false);
  const [dealAssetFieldItems, setDealAssetFieldItems]: [Record<string, AssetField> | null, Function]
    = useState<StateValueDefault>(null);

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);
  const changeItemDeps: DepsItem[] = useMemo((): DepsItem[] => [
    dealAssetUseBreakdownDefaultProperties,
  ], [dealAssetUseBreakdownDefaultProperties]);

  const dealAssetListData: Asset[] = useMemo((): Asset[] => (dealAssetData ? [dealAssetData] : []), [dealAssetData]);

  const measureUnitMap: Record<string, MeasureUnit> = useMemo((): Record<string, MeasureUnit> => (
    StructureService.getItemMap(measureUnitList, 'name')
  ), [measureUnitList]);
  const assetOwnershipStructureTypeMap: Record<string, AssetOwnershipStructureType>
    = useMemo((): Record<string, AssetOwnershipStructureType> => (
    StructureService.getItemMap(assetOwnershipStructureTypeList, 'name')
  ), [assetOwnershipStructureTypeList]);
  const portfolioMarkerColorIdList: number[] = useMemo((): number[] => (
    portfolioList && portfolioList.length ? portfolioList.map((portfolioItem: Portfolio): number | null => (
      portfolioItem.markerColor ? portfolioItem.markerColor.id : null
    )).filter((portfolioMarkerColorId: number | null): boolean => !!portfolioMarkerColorId) as number[] : []
  ), [portfolioList]);
  const dealAssetLocationItemList: LocationMapItem[] = useMemo((): LocationMapItem[] => (
    dealAssetListData && dealAssetListData.length
      ? dealAssetListData.map((assetItem: Asset): LocationMapItem => ({
        id: assetItem.id,
        title: assetItem.name || null,
        color: (assetItem.portfolio && assetItem.portfolio.markerColor && assetItem.portfolio.markerColor
          && assetItem.portfolio.markerColor.name) || null,
        link: `${ROUTE_ASSET}/${assetItem.id}`,
        amount: assetItem.amount || null,
        imageLink: assetItem.mainPhotoThumbnail || assetItem.mainPhoto,
        latitude: assetItem.latitude || null,
        longitude: assetItem.longitude || null,
      })) : []
  ), [dealAssetListData]);

  const formPrefix: string = 'dealData.dealCommonData';
  const dealAssetSizeMeasureUnit: string = dealAssetData && dealAssetData.sizeType
    && dealAssetData.sizeType.name && measureUnitMap
    && measureUnitMap[dealAssetData.sizeType.name] && (measureUnitMap[dealAssetData.sizeType.name].title || '');
  const showInPortfolioLabel: string = dealAssetData
    && FormatService.formatBooleanValue(dealAssetData.showInPortfolio);
  const asOfRightZoningLabel: string = dealAssetData
    && FormatService.formatBooleanValue(dealAssetData.asOfRightZoning, true);
  const parsedDealAssetMaturityDate: Date = dealAssetData && dealAssetData.maturityDate
    && new Date(dealAssetData.maturityDate);
  const withAssetLocations: boolean = dealAssetData
    && !!dealAssetData.latitude && !!dealAssetData.longitude;

  const dealAssetMaturityDate: string = useMemo((): string => (
    parsedDealAssetMaturityDate && FormatService.formatDateString(parsedDealAssetMaturityDate)
  ), [parsedDealAssetMaturityDate]);

  const menuItemAssetList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    optionList: assetList,
    defaultImage: AssetDefaultImage,
  }), [assetList]);

  const menuItemAssetStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetStatusList,
  }), [assetStatusList]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemContactCompanySellerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedSellers,
  }), [contactCompanyList, selectedSellers]);

  const menuItemAssetOwnershipStructureTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetOwnershipStructureTypeList,
  }), [assetOwnershipStructureTypeList]);

  const addressClickDeps: DepsItem[] = useMemo((): DepsItem[] => [isMapVisible], [isMapVisible]);

  const changeAsset: Function = useCallback((assetId: number): void => {
    if (onAssetChange) {
      onAssetChange(assetId);
    }
  }, []);

  const addNewAsset: Function = useCallback((): void => {
    setIsNewAssetFormVisible(true);

    if (onAddNewAsset) {
      onAddNewAsset();
    }
  }, []);

  const closeNewAssetForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewAssetFormVisible(false);

    if (onCancelNewAsset) {
      onCancelNewAsset();
    }
  }, []);

  const addNewItem: MouseEventHandlerFunction = useCallback((): void => {
    if (onAddNewItem) {
      onAddNewItem({
        id: StructureService.getNewItemId(dealAssetNewFieldItems || [], (dealAssetData && dealAssetData.options) || []),
        name: '',
        value: '',
      });
    }
  }, [dealAssetData, dealAssetNewFieldItems]);

  const deleteClick: Function = useCallback((assetFieldId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(assetFieldId);
    }

    if (dealAssetFieldItems) {
      const newDealAssetFieldItems: Record<string, AssetField> = { ...dealAssetFieldItems };
      delete newDealAssetFieldItems[assetFieldId];
      setDealAssetFieldItems(newDealAssetFieldItems);
    }
  }, [dealAssetFieldItems, dealAssetNewFieldItems]);

  const sellersChange: Function = useCallback((contactValues: Contact): void => {
    if (onAssetSellersChange) {
      onAssetSellersChange(contactValues);
    }

    setSelectedSellers(contactValues);
  }, []);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, []);

  const switchMapContainerVisibility: Function = useCallback((): void => {
    setIsMapVisible(!isMapVisible);
  }, [isMapVisible]);

  const switchAdditionalFormVisibility: Function = useCallback((): void => {
    setIsAdditionalFormVisible(!isAdditionalFormVisible);
  }, [isAdditionalFormVisible]);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  useEffect((): void => {
    if (dealAssetData) {
      const initialDealAssetFieldItems: Record<string, AssetField> = {};
      if (dealAssetData.options && dealAssetData.options.length) {
        dealAssetData.options.forEach((assetFieldItem: AssetField): void => {
          initialDealAssetFieldItems[assetFieldItem.id] = { ...assetFieldItem };
        });
      }
      setDealAssetFieldItems(initialDealAssetFieldItems);

      if (!selectedSellers) {
        setSelectedSellers(dealAssetData.sellers || []);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(dealAssetData.types || []);
      }
    }
  }, [dealAssetData, isEditMode]);

  useEffect((): void => {
    if (!isEditMode && !isNewDeal) {
      setIsNewAssetFormVisible(false);
    } else if (isNewDeal) {
      setIsNewAssetFormVisible(true);
    }
  }, [isEditMode, isNewDeal]);

  return (
    <CheckAccess permissions={dealCommonEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isFullWidth
        isFullHeight
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealCommonEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        <Table>
          <CheckAccess permissions={dealCommonEditPermissions} isEnabled={isEditMode || isNewDeal}>
            <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
              {
                (((isEditMode || isNewDeal) && !isNewAssetFormVisible) || (dealAssetData && dealAssetData.name)) && (
                  <TableRow>
                    <TableCell>
                      Asset
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldSelect
                            withSearch
                            withBlankElement
                            onSearch={onDealAssetSearch}
                            searchOptions={{
                              dealId: dealData && dealData.id ? dealData.id : null,
                            }}
                            isDisabled={assetList && !assetList.length}
                            name={`${formPrefix}.assetId`}
                            optionsTotal={assetList && assetList.length}
                            onChange={((assetId: number): void => {
                              changeAsset(assetId);
                            })}
                            onClose={onDealAssetSearchClose}
                            newItemComponent={(
                              <MenuItemAddNewItem
                                label="New Asset"
                                createPermission={ASSET_CREATE}
                                onClick={addNewAsset}
                              />
                            )}
                          >
                            {menuItemAssetList}
                          </FormFieldSelect>
                        ) : (dealAssetData && dealAssetData.id && dealAssetData.name && (
                          <FilteredLink
                            link={`${ROUTE_ASSET}/${dealAssetData.id}`}
                            permission={ASSET_VIEW}
                          >
                            {dealAssetData.name}
                          </FilteredLink>
                        ))
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                ((isEditMode || isNewDeal) && isNewAssetFormVisible) && (
                  <TableRow>
                    <TableCell>
                      Asset (new)
                    </TableCell>
                    <TableCell isTextRight>
                      <ActionButton onClick={closeNewAssetForm}>
                        <CancelFilledIcon />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.status)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Status
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldSelect
                            isDisabled={assetStatusList && !assetStatusList.length}
                            name={`${formPrefix}.asset.status`}
                            optionsTotal={assetStatusList && assetStatusList.length}
                          >
                            {menuItemAssetStatusList}
                          </FormFieldSelect>
                        ) : (
                          dealAssetData && dealAssetData.statusText
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && (dealAssetData.mainPhotoThumbnail || dealAssetData.mainPhoto)))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Photo
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldUpload
                            isImage
                            isDroppable
                            isSmallImage
                            name={`${formPrefix}.asset.mainPhoto`}
                            uploadFile={dealAssetPhotoFile}
                            buttonLabel={dealAssetData && dealAssetData.name}
                            defaultPreviewUrl={dealAssetIsPhotoDeleted ? AssetDefaultImage : (dealAssetData
                              && (dealAssetData.mainPhotoThumbnail || dealAssetData.mainPhoto))}
                            acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                            onFileChange={onFileChange}
                            onFileDelete={onFileDelete}
                          />
                        ) : (
                          dealAssetData && dealAssetData.name
                          && (dealAssetData.mainPhotoThumbnail || dealAssetData.mainPhoto) && (
                            <FilteredLink
                              isExternal
                              link={dealAssetData.mainPhoto || dealAssetData.mainPhotoThumbnail}
                            >
                              <Image
                                text={dealAssetData.name}
                                link={dealAssetData.mainPhotoThumbnail || dealAssetData.mainPhoto
                                  || AssetDefaultImage}
                              />
                            </FilteredLink>
                          )
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && ((dealAssetData && dealAssetData.name) || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.streetAddress)) && (
                  <TableRow>
                    <TableCell>
                      <FilteredLink
                        isSwitcher={!isEditMode && !isNewDeal && withAssetLocations}
                        clickDeps={addressClickDeps}
                        onClick={!isEditMode && !isNewDeal && withAssetLocations ? switchMapContainerVisibility : null}
                      >
                        Address
                      </FilteredLink>
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FieldAddressBlock isCompressed withOffset>
                            <FormFieldAddress
                              name={`${formPrefix}.asset.streetAddress`}
                              onAddressSearchCompleted={onAddressSearchCompleted}
                              onKeyDown={onAddressFieldKeyDown}
                            />
                            <FormFieldText isDisabled name={`${formPrefix}.asset.location`} />
                            <FormFieldText isDisabled name={`${formPrefix}.asset.postalCode`} />
                            <FormFieldText isHidden name={`${formPrefix}.asset.address`} />
                          </FieldAddressBlock>
                        ) : (
                          dealAssetData && FormatService.formatStringAddress(
                            dealAssetData.streetAddress,
                            dealAssetData.location,
                            dealAssetData.postalCode,
                          )
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible)) || (dealAssetData
                  && dealAssetData.addressDetails)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Address Details
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldText name={`${formPrefix}.asset.addressDetails`} />
                        ) : dealAssetData.addressDetails
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && !isNewDeal && isMapVisible && dealAssetLocationItemList
                && !!dealAssetLocationItemList.length && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS_TOTAL}>
                      <LocationMap
                        defaultColor={MARKER_COLOR_RED}
                        colorIdList={portfolioMarkerColorIdList}
                        locationItemList={dealAssetLocationItemList}
                      />
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && ((dealAssetData && dealAssetData.name) || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.types && !!Object.keys(dealAssetData.types).length)) && (
                  <TableRow>
                    <TableCell>
                      Property Type
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldMultiSelect
                            isDisabled={assetTypeList && !assetTypeList.length}
                            keyField="name"
                            name={`${formPrefix}.asset.types`}
                            optionList={assetTypeList}
                            changeItemDeps={changeItemDeps}
                            onValuesChange={assetTypesChange}
                            onAddItem={onAddAssetType}
                          >
                            {menuItemAssetTypeList}
                          </FormFieldMultiSelect>
                        ) : (
                          dealAssetData.types && !!Object.keys(dealAssetData.types).length
                          && dealAssetData.types.map((assetTypeItem: AssetType): ReactElement | null => (
                            assetTypeItem.id ? (
                              <Chip
                                isDisabled
                                key={assetTypeItem.id}
                                label={assetTypeItem.title}
                              />
                            ) : null
                          ))
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.size)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Building Size
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FieldSeparatedBlock>
                            <FormFieldNumber name={`${formPrefix}.asset.size`} />
                            <FormFieldSelect
                              withBlankElement
                              isDisabled={measureUnitList && !measureUnitList.length}
                              name={`${formPrefix}.asset.sizeType`}
                              optionsTotal={measureUnitList && measureUnitList.length}
                            >
                              {menuItemMeasureUnitList}
                            </FormFieldSelect>
                          </FieldSeparatedBlock>
                        ) : (
                          dealAssetData && `${dealAssetData.size} ${dealAssetSizeMeasureUnit}`
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (!isEditMode && !isNewDeal && dealAssetData && dealAssetData.ltv
                  && !isSimpleView && isAdditionalFormVisible) && (
                  <TableRow>
                    <TableCell>
                      LTV
                    </TableCell>
                    <TableCell>
                      {dealAssetData && dealAssetData.ltv}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.parcelNumber))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Location Property Identifier
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldText name={`${formPrefix}.asset.parcelNumber`} />
                        ) : dealAssetData && dealAssetData.parcelNumber
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.submarket))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Submarket
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldText name={`${formPrefix}.asset.submarket`} />
                        ) : dealAssetData && dealAssetData.submarket
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              <VisibleField
                isActive={isNewDeal}
                fieldName={DEAL_TEMPLATE_KEY_LOT_SIZE}
                hiddenFieldList={dealHiddenFieldList}
              >
                {
                  (((isEditMode || isNewDeal) && ((dealAssetData && dealAssetData.name) || isNewAssetFormVisible))
                    || (dealAssetData && dealAssetData.lotSize)) && (
                    <TableRow>
                      <TableCell>
                        Lot Size
                      </TableCell>
                      <TableCell>
                        {
                          (isEditMode || isNewDeal) ? (
                            <FormFieldText name={`${formPrefix}.asset.lotSize`} />
                          ) : dealAssetData && dealAssetData.lotSize
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
              </VisibleField>
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.amount)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Total Capitalization
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.amount`} />
                        ) : dealAssetData.amount
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.value)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Total Value
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.value`} />
                        ) : dealAssetData.value
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.sellers && !!dealAssetData.sellers.length))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Sellers
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldMultiSelect
                            withImage
                            withSearch
                            isDisabled={contactCompanyList && !contactCompanyList.length}
                            name={`${formPrefix}.asset.sellerIds`}
                            defaultImage={CompanyDefaultImage}
                            optionList={contactCompanyList}
                            onValuesChange={sellersChange}
                            onSearch={onContactCompanySearch}
                            onClose={onContactCompanySearchClose}
                          >
                            {menuItemContactCompanySellerList}
                          </FormFieldMultiSelect>
                        ) : (
                          dealAssetData.sellers && !!dealAssetData.sellers.length
                          && dealAssetData.sellers.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible)) || dealAssetData)
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Show In Portfolio
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldCheckbox name={`${formPrefix}.asset.showInPortfolio`} />
                        ) : showInPortfolioLabel
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.assetOwnershipStructure))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Ownership Structure
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldSelect
                            withBlankElement
                            isDisabled={assetOwnershipStructureTypeList && !assetOwnershipStructureTypeList.length}
                            name={`${formPrefix}.asset.assetOwnershipStructure`}
                            optionsTotal={assetOwnershipStructureTypeList && assetOwnershipStructureTypeList.length}
                          >
                            {menuItemAssetOwnershipStructureTypeList}
                          </FormFieldSelect>
                        ) : (
                          (dealAssetData && assetOwnershipStructureTypeMap[dealAssetData.assetOwnershipStructure]
                            && assetOwnershipStructureTypeMap[dealAssetData.assetOwnershipStructure].title) || ''
                        )
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.affordableUnits))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Affordable Units
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldText name={`${formPrefix}.asset.affordableUnits`} />
                        ) : dealAssetData && dealAssetData.affordableUnits
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                ((((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && !!asOfRightZoningLabel)) && !isSimpleView && isAdditionalFormVisible) && (
                  <TableRow>
                    <TableCell>
                      As-of-Right Zoning
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldCheckbox name={`${formPrefix}.asset.asOfRightZoning`} />
                        ) : (asOfRightZoningLabel || null)
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.cost)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Cost
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.cost`} />
                        ) : dealAssetData && dealAssetData.cost
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.occupancy)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Occupancy
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldText name={`${formPrefix}.asset.occupancy`} />
                        ) : dealAssetData && dealAssetData.occupancy
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.currentNoi)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Current NOI
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.currentNoi`} />
                        ) : dealAssetData && dealAssetData.currentNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.ttmNoi)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      TTM NOI
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.ttmNoi`} />
                        ) : dealAssetData && dealAssetData.ttmNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.runRateNoi)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Run Rate NOI
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.runRateNoi`} />
                        ) : dealAssetData && dealAssetData.runRateNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.forwardNoi))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Forward NOI
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.forwardNoi`} />
                        ) : dealAssetData && dealAssetData.forwardNoi
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetMaturityDate)) && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Maturity Date
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldDate withBlankValues name={`${formPrefix}.asset.maturityDate`} />
                        ) : dealAssetData && dealAssetMaturityDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                ((((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                    && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.portfolioInvestedEquity))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Portfolio Invested Equity
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber name={`${formPrefix}.asset.portfolioInvestedEquity`} />
                        ) : (dealAssetData && dealAssetData.portfolioInvestedEquity)
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.leveredIrr))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Levered IRR
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber isFloat isPercent name={`${formPrefix}.asset.leveredIrr`} />
                        ) : (dealAssetData && dealAssetData.leveredIrr)
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (((isEditMode || isNewDeal) && (dealAssetData || isNewAssetFormVisible))
                  || (dealAssetData && dealAssetData.equityMultiple))
                && !isSimpleView && isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell>
                      Equity Multiple
                    </TableCell>
                    <TableCell>
                      {
                        (isEditMode || isNewDeal) ? (
                          <FormFieldNumber isFloat name={`${formPrefix}.asset.equityMultiple`} />
                        ) : (dealAssetData && dealAssetData.equityMultiple)
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isSimpleView && (dealAssetData || isNewAssetFormVisible) && !isAdditionalFormVisible && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS_TOTAL}>
                      <FilteredLink isSwitcher onClick={switchAdditionalFormVisibility}>
                        Show more
                      </FilteredLink>
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </CheckAccess>
          {
            (isAdditionalFormVisible || isSimpleView) && (
              <TableBody isPlain withZebraRows withHeaderColumn>
                {
                  [...((dealAssetFieldItems && Object.values(dealAssetFieldItems)) || []),
                    ...(dealAssetNewFieldItems || [])]
                    .map((assetFieldItem: AssetField): ReactElement => {
                      const isNewItem: boolean = dealAssetNewFieldItems && !!dealAssetNewFieldItems.length
                        && !!dealAssetNewFieldItems.find((assetNewFieldItem: AssetField): boolean => (
                          assetNewFieldItem.id === assetFieldItem.id
                        ));
                      const formKeyAssetFieldItem: string = FormService.getIndexedFormFieldKey(assetFieldItem.id);

                      return (
                        <TableRow isActive={isNewItem && !isNewDeal} key={assetFieldItem.id}>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <Fragment>
                                  <FormFieldText
                                    isHidden
                                    name={`${formPrefix}.asset.options[${formKeyAssetFieldItem}].id`}
                                  />
                                  <FormFieldText
                                    name={`${formPrefix}.asset.options[${formKeyAssetFieldItem}].name`}
                                    placeholder="Name"
                                  />
                                </Fragment>
                              ) : assetFieldItem.name
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewDeal) ? (
                                <FlexContainer>
                                  <FormFieldText
                                    name={`${formPrefix}.asset.options[${formKeyAssetFieldItem}].value`}
                                    placeholder="Value"
                                  />
                                  <div>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        isInline
                                        withNoPadding
                                        onClick={(): void => {
                                          deleteClick(assetFieldItem.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </div>
                                </FlexContainer>
                              ) : assetFieldItem.value
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })
                }
                {
                  (isEditMode || isNewDeal) && !isSimpleView && isAdditionalFormVisible && (
                    <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={addNewItem} />
                  )
                }
                {
                  !isSimpleView && (dealAssetData || isNewAssetFormVisible) && (
                    <TableRow>
                      <TableCell colSpan={COLUMNS_TOTAL}>
                        <FilteredLink isSwitcher onClick={switchAdditionalFormVisibility}>
                          Show less
                        </FilteredLink>
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            )
          }
        </Table>
        <VisibleField
          isActive={isNewDeal}
          fieldName={DEAL_TEMPLATE_KEY_DEAL_BUILDING_DETAILS_ASSET_USE_BREAKDOWN}
          hiddenFieldList={dealHiddenFieldList}
        >
          {
            (isEditMode || isNewDeal || (dealBuildingDetailsData && dealBuildingDetailsData.assetUseBreakdown
              && !!dealBuildingDetailsData.assetUseBreakdown.length)) && (
              <DealAssetUseBreakdown
                isEditMode={isEditMode}
                isNewDeal={isNewDeal}
                dealBuildingDetailsData={dealBuildingDetailsData}
                dealAssetUseBreakdownNewItems={dealAssetUseBreakdownNewItems}
                dealAssetUseBreakdownNewFieldItems={dealAssetUseBreakdownNewFieldItems}
                dealAssetUseBreakdownDefaultProperties={dealAssetUseBreakdownDefaultProperties}
                dealAssetUseBreakdownValues={dealAssetUseBreakdownValues}
                assetTypeList={assetTypeList}
                measureUnitList={measureUnitList}
                onAssetUseBreakdownAddNewItem={onAssetUseBreakdownAddNewItem}
                onAssetUseBreakdownDeleteClick={onAssetUseBreakdownDeleteClick}
                onAssetUseBreakdownAddNewField={onAssetUseBreakdownAddNewField}
                onAssetUseBreakdownDeleteField={onAssetUseBreakdownDeleteField}
                onAssetTypeChange={onAssetUseBreakdownAssetTypeChange}
              />
            )
          }
        </VisibleField>
      </Widget>
    </CheckAccess>
  );
});

export default DealPropertyDetails;
